import { useEffect, useState } from "react";
import DataTable, { Column } from "../../../components/DataTable";
import ToggleSwitch from "../../../components/ToggleSwitch";
import StaffApis from "../../../api/staffApis";
import { Subjects } from "../../../types/schoolBranchType";
import { toast } from "sonner";
import { useEditMode } from "../../../context/EditModeContext";
import useConfirmToast from "../../../util/hooks/useConfirmToast";
import { useLocation } from "react-router-dom";

export default function AssignSubject({ staffId }: { staffId?: number }) {
  const { isEditing } = useEditMode();
  const { confirm, modal } = useConfirmToast();

  const location = useLocation();
  const tab = new URLSearchParams(location.search).get("tab");

  const shouldHideLabel =
    location.pathname === "/view-profile" && tab === "Assigned Subjects";

  const [subjects, setSubjects] = useState<Subjects[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!staffId) return;

    const fetchSubjects = async () => {
      setLoading(true);
      const response = await StaffApis.getStaffSubjects(staffId);
      if (response?.status === 1) {
        setSubjects(response.data);
      }
      setLoading(false);
    };

    fetchSubjects();
  }, [staffId]);

  const handleToggle = async (subject: Subjects) => {
    setSubjects((prev) =>
      prev.map((s) =>
        s.subjectId === subject.subjectId ? { ...s, active: !s.active } : s,
      ),
    );

    try {
      const response = await StaffApis.toggleStaffSubject(
        staffId,
        subject.subjectId,
      );

      if (response?.status === 1) {
        toast.success(
          subject.active
            ? "Subject disabled successfully"
            : "Subject enabled successfully",
        );
      } else {
        throw new Error();
      }
    } catch {
      // rollback on error
      setSubjects((prev) =>
        prev.map((s) =>
          s.subjectId === subject.subjectId
            ? { ...s, active: subject.active }
            : s,
        ),
      );

      toast.error("Failed to update subject access");
    }
  };

  const columns: Column<Subjects>[] = [
    {
      label: "SL NO.",
      accessor: "subjectId",
      render : (_,index) => (
        <p>{index + 1}</p>
      )
    },
    {
      label: "Subjects List",
      accessor: "",
      searchable: true,
      render: (item) => (
        <div className="font-bold text-sm text-neutral-400">
          {item.subjectName}
        </div>
      ),
    },
    {
      label: "Assign",
      accessor: "active",
      searchable: false,
      render: (item) => (
        <ToggleSwitch
          defaultChecked={item.active}
          disabled={!isEditing}
          confirmToggle={(next) =>
            confirm(
              next
                ? "Are you sure you want to enable this Subject?"
                : "Are you sure you want to disable this Subject?",
              {
                confirmText: "yes",
              },
            )
          }
          onToggle={() => handleToggle(item)}
        />
      ),
    },
  ];

  return (
    <div className="px-4 md:px-9 py-7 flex flex-col gap-8">
      {!shouldHideLabel && (
        <label className="text-xs font-medium text-error-500">
          *Your staff will have access to only those subjects which are enabled
          here.
        </label>
      )}
      <div className="">
        <DataTable columns={columns} data={subjects} isLoading={loading} />
      </div>
      {modal}
    </div>
  );
}
